'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { seedChartHistory } from '@/lib/api/chart-history/cache';
import type { HistoryByOutcome } from '@/lib/api/chart-history/types';
import { OrderFormState, useOrderFormStore } from '@/lib/store/order-form-store';
import { usePricesStoreBase } from '@/lib/store/prices-store';
import type { EventPrices } from '@/lib/types/websocket';
import { eventStartSeconds } from '@/lib/utils/chartTime';

type ServerToStoreProps = Partial<OrderFormState> & {
  initialPrices?: EventPrices[];
  initialChartData?: HistoryByOutcome;
};

export const ServerToStore = ({
  event,
  selectedOutcomeId,
  initialPrices,
  initialChartData,
}: ServerToStoreProps) => {
  const [initialised, setInitialised] = useState(false);
  const queryClient = useQueryClient();
  const setAll = useOrderFormStore.use.setAll();
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (initialised || hydratedRef.current) {
      return;
    }
    hydratedRef.current = true;

    // Before setAll: the chart mounts its queries once the event lands and must find the seed.
    // A resolved event's SSR history runs past the resolve, so it is fetched instead.
    if (initialChartData && event && !event.resolvedAt) {
      seedChartHistory(
        queryClient,
        event.id,
        eventStartSeconds(undefined, event.createdAt),
        initialChartData,
      );
    }

    setAll({ event, selectedOutcomeId });

    if (initialPrices && initialPrices.length > 0) {
      usePricesStoreBase.getState().upsertMany(initialPrices);
    }

    setInitialised(true);
  }, [event, initialised, selectedOutcomeId, setAll, initialPrices, initialChartData, queryClient]);

  return null;
};
